import React, { useState, useEffect } from 'react';
import SMSF_RATES_CONFIG from './SMSF_Rates_Config.js';
import jsPDF from 'jspdf';

const SMSFAdministrationManager = ({ smsfState, setSmsfState }) => {
    const [activeSection, setActiveSection] = useState('overview');
    const [trusteeTasks, setTrusteeTasks] = useState([]);
    const [complianceCalendar, setComplianceCalendar] = useState([]);
    const [documentChecklist, setDocumentChecklist] = useState([]);
    const [atoRequirements, setAtoRequirements] = useState([]);
    
    // Meeting Minutes
    const [meetingMinutes, setMeetingMinutes] = useState([]);
    const [showMeetingForm, setShowMeetingForm] = useState(false);
    const [newMinute, setNewMinute] = useState({ date: '', attendees: '', agenda: '', decisions: '' });
    
    // Trustee Resolutions
    const [resolutions, setResolutions] = useState([]);
    const [showResolutionForm, setShowResolutionForm] = useState(false);
    const [newResolution, setNewResolution] = useState({ date: '', title: '', description: '', status: 'pending' });
    
    // Documents
    const [documents, setDocuments] = useState([]);
    
    // Tasks
    const [tasks, setTasks] = useState([]);
    const [showTaskForm, setShowTaskForm] = useState(false);
    const [newTask, setNewTask] = useState({ title: '', dueDate: '', priority: 'medium', reminder: false });

    // Trustee responsibilities and tasks
    const trusteeResponsibilities = [
        {
            id: 'investment_strategy',
            title: 'Investment Strategy Management',
            description: 'Develop, implement, and review investment strategy',
            frequency: 'annually',
            priority: 'high',
            lastCompleted: '2024-01-15',
            nextDue: '2025-01-15',
            status: 'completed',
            tasks: [
                'Review and update investment strategy document',
                'Assess member circumstances and risk tolerance',
                'Consider diversification requirements',
                'Document investment decisions and rationale',
                'Review asset allocation against strategy'
            ]
        },
        {
            id: 'record_keeping',
            title: 'Record Keeping & Documentation',
            description: 'Maintain comprehensive SMSF records',
            frequency: 'ongoing',
            priority: 'high',
            lastCompleted: '2024-12-15',
            nextDue: 'ongoing',
            status: 'in_progress',
            tasks: [
                'Maintain member contribution records',
                'Document all investment transactions',
                'Keep bank statements and investment confirmations',
                'Maintain trustee minutes and resolutions',
                'Store valuation reports and appraisals'
            ]
        },
        {
            id: 'compliance_monitoring',
            title: 'Compliance Monitoring',
            description: 'Monitor SMSF compliance with superannuation law',
            frequency: 'ongoing',
            priority: 'high',
            lastCompleted: '2024-12-15',
            nextDue: 'ongoing',
            status: 'in_progress',
            tasks: [
                'Monitor contribution caps compliance',
                'Check in-house asset restrictions',
                'Verify related party transaction compliance',
                'Ensure sole purpose test compliance',
                'Monitor borrowing restrictions'
            ]
        },
        {
            id: 'annual_return',
            title: 'Annual Return Preparation',
            description: 'Prepare and lodge SMSF annual return',
            frequency: 'annually',
            priority: 'critical',
            lastCompleted: '2024-05-15',
            nextDue: '2025-05-15',
            status: 'pending',
            tasks: [
                'Prepare financial statements',
                'Complete member benefit statements',
                'Lodge annual return with ATO',
                'Pay any outstanding tax liabilities',
                'Provide copies to members'
            ]
        },
        {
            id: 'audit_management',
            title: 'Audit Management',
            description: 'Manage SMSF audit process',
            frequency: 'annually',
            priority: 'critical',
            lastCompleted: '2024-05-15',
            nextDue: '2025-05-15',
            status: 'pending',
            tasks: [
                'Engage qualified SMSF auditor',
                'Provide audit documentation',
                'Respond to audit queries',
                'Implement audit recommendations',
                'Maintain audit reports'
            ]
        },
        {
            id: 'member_communications',
            title: 'Member Communications',
            description: 'Communicate with SMSF members',
            frequency: 'annually',
            priority: 'medium',
            lastCompleted: '2024-05-15',
            nextDue: '2025-05-15',
            status: 'pending',
            tasks: [
                'Provide annual member statements',
                'Communicate contribution limits',
                'Advise on investment changes',
                'Notify of regulatory changes',
                'Maintain member contact details'
            ]
        }
    ];

    // Compliance calendar events
    const complianceEvents = [
        {
            id: 1,
            title: 'Monthly Bank Reconciliation',
            description: 'Reconcile all bank accounts and investment statements',
            frequency: 'monthly',
            dueDate: '2025-01-31',
            priority: 'medium',
            status: 'pending',
            category: 'record_keeping'
        },
        {
            id: 2,
            title: 'Quarterly Investment Review',
            description: 'Review investment performance and rebalance if needed',
            frequency: 'quarterly',
            dueDate: '2025-03-31',
            priority: 'medium',
            status: 'pending',
            category: 'investment_strategy'
        },
        {
            id: 3,
            title: 'Annual Valuation Update',
            description: 'Update asset valuations and document valuation methods',
            frequency: 'annually',
            dueDate: '2025-06-30',
            priority: 'high',
            status: 'pending',
            category: 'record_keeping'
        },
        {
            id: 4,
            title: 'Trustee Minutes Review',
            description: 'Document trustee decisions and meetings',
            frequency: 'as_needed',
            dueDate: '2025-01-15',
            priority: 'low',
            status: 'pending',
            category: 'governance'
        },
        {
            id: 5,
            title: 'Contribution Cap Review',
            description: 'Review and document contribution cap usage',
            frequency: 'annually',
            dueDate: '2025-06-30',
            priority: 'high',
            status: 'pending',
            category: 'compliance'
        },
        {
            id: 6,
            title: 'Investment Strategy Review',
            description: 'Annual investment strategy review and update',
            frequency: 'annually',
            dueDate: '2025-06-30',
            priority: 'high',
            status: 'pending',
            category: 'investment_strategy'
        }
    ];

    // Document checklist
    const documentRequirements = [
        {
            id: 'trust_deed',
            title: 'Trust Deed',
            description: 'Current SMSF trust deed and any amendments',
            required: true,
            status: 'completed',
            lastUpdated: '2024-01-15',
            nextReview: '2025-01-15'
        },
        {
            id: 'investment_strategy',
            title: 'Investment Strategy Document',
            description: 'Written investment strategy and reviews',
            required: true,
            status: 'completed',
            lastUpdated: '2024-01-15',
            nextReview: '2025-01-15'
        },
        {
            id: 'member_agreements',
            title: 'Member Agreements',
            description: 'Member application forms and agreements',
            required: true,
            status: 'completed',
            lastUpdated: '2020-01-15',
            nextReview: '2025-01-15'
        },
        {
            id: 'trustee_minutes',
            title: 'Trustee Minutes',
            description: 'Minutes of trustee meetings and resolutions',
            required: true,
            status: 'in_progress',
            lastUpdated: '2024-12-15',
            nextReview: 'ongoing'
        },
        {
            id: 'financial_statements',
            title: 'Financial Statements',
            description: 'Annual financial statements and audit reports',
            required: true,
            status: 'completed',
            lastUpdated: '2024-05-15',
            nextReview: '2025-05-15'
        },
        {
            id: 'valuation_reports',
            title: 'Valuation Reports',
            description: 'Asset valuation reports and appraisals',
            required: true,
            status: 'in_progress',
            lastUpdated: '2024-01-15',
            nextReview: '2025-01-15'
        },
        {
            id: 'insurance_documents',
            title: 'Insurance Documents',
            description: 'Insurance policies and coverage details',
            required: false,
            status: 'completed',
            lastUpdated: '2024-01-15',
            nextReview: '2025-01-15'
        },
        {
            id: 'borrowing_documents',
            title: 'Borrowing Documents',
            description: 'LRBA documents and loan agreements',
            required: false,
            status: 'not_applicable',
            lastUpdated: null,
            nextReview: null
        }
    ];

    // ATO requirements and obligations
    const atoObligations = [
        {
            id: 'annual_return',
            title: 'SMSF Annual Return',
            description: 'Lodge SMSF annual return with ATO',
            dueDate: '2025-05-15',
            frequency: 'annually',
            penalty: '$210 per penalty unit',
            status: 'pending',
            priority: 'critical'
        },
        {
            id: 'member_statements',
            title: 'Member Benefit Statements',
            description: 'Provide annual member benefit statements',
            dueDate: '2025-05-15',
            frequency: 'annually',
            penalty: '$210 per penalty unit',
            status: 'pending',
            priority: 'critical'
        },
        {
            id: 'contribution_reporting',
            title: 'Contribution Reporting',
            description: 'Report contributions to ATO',
            dueDate: 'ongoing',
            frequency: 'as_required',
            penalty: 'Late payment interest',
            status: 'compliant',
            priority: 'high'
        },
        {
            id: 'event_based_reporting',
            title: 'Event-Based Reporting',
            description: 'Report significant events to ATO',
            dueDate: 'as_required',
            frequency: 'as_required',
            penalty: '$210 per penalty unit',
            status: 'compliant',
            priority: 'high'
        },
        {
            id: 'transfer_balance_cap',
            title: 'Transfer Balance Cap Reporting',
            description: 'Report transfer balance cap events',
            dueDate: 'as_required',
            frequency: 'as_required',
            penalty: 'Excess transfer balance tax',
            status: 'compliant',
            priority: 'high'
        }
    ];

    // Initialize data
    useEffect(() => {
        setTrusteeTasks(trusteeResponsibilities);
        setComplianceCalendar(complianceEvents);
        setDocumentChecklist(documentRequirements);
        setAtoRequirements(atoObligations);
    }, []);

    // Calculate completion statistics
    const calculateCompletionStats = () => {
        const totalTasks = trusteeResponsibilities.length;
        const completedTasks = trusteeResponsibilities.filter(task => task.status === 'completed').length;
        const inProgressTasks = trusteeResponsibilities.filter(task => task.status === 'in_progress').length;
        const pendingTasks = trusteeResponsibilities.filter(task => task.status === 'pending').length;
        
        return {
            total: totalTasks,
            completed: completedTasks,
            inProgress: inProgressTasks,
            pending: pendingTasks,
            completionRate: Math.round((completedTasks / totalTasks) * 100)
        };
    };

    const completionStats = calculateCompletionStats();

    // Get priority color
    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'critical': return 'text-red-600 bg-red-100';
            case 'high': return 'text-orange-600 bg-orange-100';
            case 'medium': return 'text-yellow-600 bg-yellow-100';
            case 'low': return 'text-blue-600 bg-blue-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    // Get status color
    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'text-green-600 bg-green-100';
            case 'in_progress': return 'text-blue-600 bg-blue-100';
            case 'pending': return 'text-yellow-600 bg-yellow-100';
            case 'not_applicable': return 'text-gray-600 bg-gray-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const renderOverview = () => (
        <div className="space-y-6">
            {/* Administration Overview */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Administration Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                            {completionStats.total}
                        </div>
                        <div className="text-sm text-blue-700">Total Responsibilities</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                            {completionStats.completed}
                        </div>
                        <div className="text-sm text-green-700">Completed</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                            {completionStats.inProgress}
                        </div>
                        <div className="text-sm text-blue-700">In Progress</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                        <div className="text-2xl font-bold text-yellow-600">
                            {completionStats.completionRate}%
                        </div>
                        <div className="text-sm text-yellow-700">Completion Rate</div>
                    </div>
                </div>
            </div>

            {/* Upcoming Deadlines */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Upcoming Deadlines</h3>
                <div className="space-y-3">
                    {complianceEvents
                        .filter(event => new Date(event.dueDate) <= new Date(Date.now() + 90 * 24 * 60 * 60 * 1000))
                        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
                        .slice(0, 5)
                        .map((event) => (
                            <div key={event.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                <div>
                                    <h4 className="font-medium text-gray-800">{event.title}</h4>
                                    <p className="text-sm text-gray-600">{event.description}</p>
                                </div>
                                <div className="text-right">
                                    <div className={`px-2 py-1 text-xs rounded ${getPriorityColor(event.priority)}`}>
                                        {event.priority.toUpperCase()}
                                    </div>
                                    <div className="text-sm text-gray-500 mt-1">
                                        Due: {new Date(event.dueDate).toLocaleDateString()}
                                    </div>
                                </div>
                            </div>
                        ))
                    }
                </div>
            </div>

            {/* Critical Issues */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Critical Issues Requiring Attention</h3>
                <div className="space-y-3">
                    {trusteeResponsibilities
                        .filter(task => task.priority === 'critical' && task.status !== 'completed')
                        .map((task) => (
                            <div key={task.id} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-start">
                                    <span className="text-red-600 mr-2">🚨</span>
                                    <div>
                                        <h4 className="font-medium text-red-800">{task.title}</h4>
                                        <p className="text-sm text-red-700">{task.description}</p>
                                        <p className="text-xs text-red-600 mt-1">
                                            Due: {new Date(task.nextDue).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))
                    }
                    {trusteeResponsibilities.filter(task => task.priority === 'critical' && task.status !== 'completed').length === 0 && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                            <span className="text-green-600">✅ No critical issues requiring immediate attention</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderTrusteeResponsibilities = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Trustee Responsibilities</h3>
                <div className="space-y-4">
                    {trusteeResponsibilities.map((responsibility) => (
                        <div key={responsibility.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-3">
                                <div>
                                    <h4 className="font-semibold text-gray-800">{responsibility.title}</h4>
                                    <p className="text-sm text-gray-600">{responsibility.description}</p>
                                </div>
                                <div className="flex space-x-2">
                                    <span className={`px-2 py-1 text-xs rounded ${getPriorityColor(responsibility.priority)}`}>
                                        {responsibility.priority.toUpperCase()}
                                    </span>
                                    <span className={`px-2 py-1 text-xs rounded ${getStatusColor(responsibility.status)}`}>
                                        {responsibility.status.replace('_', ' ').toUpperCase()}
                                    </span>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                <div className="text-sm">
                                    <span className="text-gray-500">Frequency:</span>
                                    <span className="ml-2 font-medium">{responsibility.frequency}</span>
                                </div>
                                <div className="text-sm">
                                    <span className="text-gray-500">Next Due:</span>
                                    <span className="ml-2 font-medium">{responsibility.nextDue}</span>
                                </div>
                            </div>

                            <div className="mt-3">
                                <h5 className="font-medium text-gray-700 mb-2">Key Tasks:</h5>
                                <ul className="space-y-1">
                                    {responsibility.tasks.map((task, index) => (
                                        <li key={index} className="flex items-center text-sm text-gray-600">
                                            <span className="mr-2">•</span>
                                            {task}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderComplianceCalendar = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Compliance Calendar</h3>
                <div className="space-y-4">
                    {complianceEvents.map((event) => (
                        <div key={event.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h4 className="font-semibold text-gray-800">{event.title}</h4>
                                    <p className="text-sm text-gray-600">{event.description}</p>
                                </div>
                                <div className="flex space-x-2">
                                    <span className={`px-2 py-1 text-xs rounded ${getPriorityColor(event.priority)}`}>
                                        {event.priority.toUpperCase()}
                                    </span>
                                    <span className={`px-2 py-1 text-xs rounded ${getStatusColor(event.status)}`}>
                                        {event.status.toUpperCase()}
                                    </span>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                <div>
                                    <span className="text-gray-500">Due Date:</span>
                                    <span className="ml-2 font-medium">{new Date(event.dueDate).toLocaleDateString()}</span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Frequency:</span>
                                    <span className="ml-2 font-medium">{event.frequency}</span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Category:</span>
                                    <span className="ml-2 font-medium">{event.category.replace('_', ' ')}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderDocumentChecklist = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Document Checklist</h3>
                <div className="space-y-4">
                    {documentRequirements.map((doc) => (
                        <div key={doc.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h4 className="font-semibold text-gray-800">{doc.title}</h4>
                                    <p className="text-sm text-gray-600">{doc.description}</p>
                                </div>
                                <div className="flex space-x-2">
                                    {doc.required && (
                                        <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800">
                                            REQUIRED
                                        </span>
                                    )}
                                    <span className={`px-2 py-1 text-xs rounded ${getStatusColor(doc.status)}`}>
                                        {doc.status.replace('_', ' ').toUpperCase()}
                                    </span>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-gray-500">Last Updated:</span>
                                    <span className="ml-2 font-medium">
                                        {doc.lastUpdated ? new Date(doc.lastUpdated).toLocaleDateString() : 'Never'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Next Review:</span>
                                    <span className="ml-2 font-medium">
                                        {doc.nextReview ? new Date(doc.nextReview).toLocaleDateString() : 'Ongoing'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderATORequirements = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">ATO Requirements & Obligations</h3>
                <div className="space-y-4">
                    {atoObligations.map((obligation) => (
                        <div key={obligation.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h4 className="font-semibold text-gray-800">{obligation.title}</h4>
                                    <p className="text-sm text-gray-600">{obligation.description}</p>
                                </div>
                                <div className="flex space-x-2">
                                    <span className={`px-2 py-1 text-xs rounded ${getPriorityColor(obligation.priority)}`}>
                                        {obligation.priority.toUpperCase()}
                                    </span>
                                    <span className={`px-2 py-1 text-xs rounded ${getStatusColor(obligation.status)}`}>
                                        {obligation.status.toUpperCase()}
                                    </span>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                <div>
                                    <span className="text-gray-500">Due Date:</span>
                                    <span className="ml-2 font-medium">{obligation.dueDate}</span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Frequency:</span>
                                    <span className="ml-2 font-medium">{obligation.frequency}</span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Penalty:</span>
                                    <span className="ml-2 font-medium text-red-600">{obligation.penalty}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ATO Contact Information */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">ATO Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-2">SMSF Helpline</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                            <li>Phone: 13 10 20</li>
                            <li>Monday to Friday: 8am to 6pm</li>
                            <li>Email: smsf@ato.gov.au</li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Online Services</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                            <li>SMSF Annual Return: Online services</li>
                            <li>Member Contributions: Online services</li>
                            <li>Event-Based Reporting: Online services</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderMeetingMinutes = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Meeting Minutes</h3>
                    <button
                        onClick={() => setShowMeetingForm(!showMeetingForm)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        {showMeetingForm ? 'Cancel' : 'Create New Minutes'}
                    </button>
                </div>
                
                {showMeetingForm && (
                    <div className="p-4 bg-gray-50 rounded-lg mb-4">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Date</label>
                                <input
                                    type="date"
                                    value={newMinute.date}
                                    onChange={(e) => setNewMinute({ ...newMinute, date: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Attendees</label>
                                <textarea
                                    value={newMinute.attendees}
                                    onChange={(e) => setNewMinute({ ...newMinute, attendees: e.target.value })}
                                    rows="2"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="List all trustees present..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Agenda</label>
                                <textarea
                                    value={newMinute.agenda}
                                    onChange={(e) => setNewMinute({ ...newMinute, agenda: e.target.value })}
                                    rows="3"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="Meeting agenda items..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Decisions Made</label>
                                <textarea
                                    value={newMinute.decisions}
                                    onChange={(e) => setNewMinute({ ...newMinute, decisions: e.target.value })}
                                    rows="3"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="Key decisions and resolutions..."
                                />
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={() => {
                                    if (newMinute.date && newMinute.attendees) {
                                        setMeetingMinutes([...meetingMinutes, { ...newMinute, id: Date.now() }]);
                                        setNewMinute({ date: '', attendees: '', agenda: '', decisions: '' });
                                        setShowMeetingForm(false);
                                        alert('Meeting minutes saved with date: ' + newMinute.date);
                                    }
                                }}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                            >
                                Save Minutes
                            </button>
                        </div>
                    </div>
                )}
                
                {meetingMinutes.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p>No meeting minutes recorded</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {meetingMinutes.map((minute) => (
                            <div key={minute.id} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="font-semibold text-blue-800">Meeting: {minute.date}</div>
                                <p className="text-sm text-gray-700 mt-1"><strong>Attendees:</strong> {minute.attendees}</p>
                                <p className="text-sm text-gray-700"><strong>Agenda:</strong> {minute.agenda}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
    
    const renderResolutions = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Trustee Resolutions</h3>
                    <button
                        onClick={() => setShowResolutionForm(!showResolutionForm)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        {showResolutionForm ? 'Cancel' : 'Create Resolution'}
                    </button>
                </div>
                
                {showResolutionForm && (
                    <div className="p-4 bg-gray-50 rounded-lg mb-4">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Resolution Date</label>
                                <input
                                    type="date"
                                    value={newResolution.date}
                                    onChange={(e) => setNewResolution({ ...newResolution, date: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Resolution Title</label>
                                <input
                                    type="text"
                                    value={newResolution.title}
                                    onChange={(e) => setNewResolution({ ...newResolution, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="e.g., Approve investment in XYZ shares"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                <textarea
                                    value={newResolution.description}
                                    onChange={(e) => setNewResolution({ ...newResolution, description: e.target.value })}
                                    rows="3"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="Full resolution details..."
                                />
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={() => {
                                    if (newResolution.title) {
                                        setResolutions([...resolutions, { ...newResolution, id: Date.now() }]);
                                        setNewResolution({ date: '', title: '', description: '', status: 'pending' });
                                        setShowResolutionForm(false);
                                    }
                                }}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                            >
                                Save Resolution
                            </button>
                        </div>
                    </div>
                )}
                
                {resolutions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p>No trustee resolutions recorded</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {resolutions.map((res) => (
                            <div key={res.id} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="font-semibold text-blue-800">{res.title}</div>
                                <p className="text-sm text-gray-700 mt-1">Date: {res.date}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
    
    const renderDocumentStorage = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Document Storage</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Upload Document</label>
                        <input
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.png"
                            onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                    const file = e.target.files[0];
                                    setDocuments([...documents, { id: Date.now(), name: file.name, date: new Date().toISOString().split('T')[0] }]);
                                    alert(`Document "${file.name}" uploaded and stored successfully`);
                                }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    
                    {documents.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <p>No documents uploaded</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {documents.map((doc) => (
                                <div key={doc.id} className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex justify-between items-center">
                                    <div>
                                        <div className="font-medium">{doc.name}</div>
                                        <div className="text-xs text-gray-500">Uploaded: {doc.date}</div>
                                    </div>
                                    <button
                                        onClick={() => setDocuments(documents.filter(d => d.id !== doc.id))}
                                        className="text-red-600 hover:text-red-800 text-sm"
                                    >
                                        Delete
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
    
    const renderTasks = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Task List</h3>
                    <button
                        onClick={() => setShowTaskForm(!showTaskForm)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        {showTaskForm ? 'Cancel' : 'Add Task'}
                    </button>
                </div>
                
                {showTaskForm && (
                    <div className="p-4 bg-gray-50 rounded-lg mb-4">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Task Title</label>
                                <input
                                    type="text"
                                    value={newTask.title}
                                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="e.g., Review investment strategy"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                                    <input
                                        type="date"
                                        value={newTask.dueDate}
                                        onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                                    <select
                                        value={newTask.priority}
                                        onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                    </select>
                                </div>
                            </div>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={newTask.reminder}
                                    onChange={(e) => setNewTask({ ...newTask, reminder: e.target.checked })}
                                    className="mr-2 h-4 w-4"
                                />
                                <span className="text-sm">Set reminder</span>
                            </label>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={() => {
                                    if (newTask.title) {
                                        setTasks([...tasks, { ...newTask, id: Date.now() }]);
                                        setNewTask({ title: '', dueDate: '', priority: 'medium', reminder: false });
                                        setShowTaskForm(false);
                                    }
                                }}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                            >
                                Add Task
                            </button>
                        </div>
                    </div>
                )}
                
                {tasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p>No tasks added</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {tasks.map((task) => (
                            <div key={task.id} className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex justify-between items-center">
                                <div>
                                    <div className="font-medium">{task.title}</div>
                                    <div className="text-xs text-gray-500">Due: {task.dueDate} | Priority: {task.priority}</div>
                                </div>
                                <button
                                    onClick={() => setTasks(tasks.filter(t => t.id !== task.id))}
                                    className="text-red-600 hover:text-red-800 text-sm"
                                >
                                    Delete
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
    
    const renderCommunications = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Member Communications</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Communication Type</label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-md">
                            <option>Annual Member Statement</option>
                            <option>Contribution Notice</option>
                            <option>Investment Update</option>
                            <option>Regulatory Change Notice</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Recipient(s)</label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="Member name(s)"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                        <textarea
                            rows="4"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="Communication content..."
                        />
                    </div>
                    <div className="flex justify-end space-x-2">
                        <button
                            onClick={() => alert('Email sent to member(s)')}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Send Email
                        </button>
                        <button
                            onClick={() => alert('PDF notice generated')}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                            Generate PDF
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Administration Manager</h2>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setActiveSection('overview')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeSection === 'overview' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveSection('trustee')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeSection === 'trustee' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Trustee Responsibilities
                    </button>
                    <button
                        onClick={() => setActiveSection('calendar')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeSection === 'calendar' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Compliance Calendar
                    </button>
                    <button
                        onClick={() => setActiveSection('documents')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeSection === 'documents' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Document Checklist
                    </button>
                    <button
                        onClick={() => setActiveSection('ato')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeSection === 'ato' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        ATO Requirements
                    </button>
                    <button
                        onClick={() => setActiveSection('minutes')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeSection === 'minutes' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Meeting Minutes
                    </button>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Resolutions tab clicked');
                            setActiveSection('resolutions');
                        }}
                        className={`px-4 py-2 rounded-md transition-colors cursor-pointer z-10 ${
                            activeSection === 'resolutions' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        Resolutions
                    </button>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Documents tab clicked');
                            setActiveSection('uploads');
                        }}
                        className={`px-4 py-2 rounded-md transition-colors cursor-pointer z-10 ${
                            activeSection === 'uploads' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        Documents
                    </button>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Tasks tab clicked');
                            setActiveSection('tasks');
                        }}
                        className={`px-4 py-2 rounded-md transition-colors cursor-pointer z-10 ${
                            activeSection === 'tasks' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        Tasks
                    </button>
                    <button
                        onClick={() => setActiveSection('communications')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeSection === 'communications' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Communications
                    </button>
                </div>
            </div>

            {/* Content based on active section */}
            {activeSection === 'overview' && renderOverview()}
            {activeSection === 'trustee' && renderTrusteeResponsibilities()}
            {activeSection === 'calendar' && renderComplianceCalendar()}
            {activeSection === 'documents' && renderDocumentChecklist()}
            {activeSection === 'ato' && renderATORequirements()}
            {activeSection === 'minutes' && renderMeetingMinutes()}
            {activeSection === 'resolutions' && renderResolutions()}
            {activeSection === 'uploads' && renderDocumentStorage()}
            {activeSection === 'tasks' && renderTasks()}
            {activeSection === 'communications' && renderCommunications()}

            {/* Action Buttons */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        Last updated: {new Date().toLocaleDateString()}
                    </div>
                    <div className="flex space-x-4">
                        <button 
                            onClick={() => {
                                const doc = new jsPDF();
                                doc.setFontSize(18);
                                doc.setFont(undefined, 'bold');
                                doc.text('SMSF Administration Report', 105, 20, { align: 'center' });
                                doc.setFontSize(10);
                                doc.setFont(undefined, 'normal');
                                doc.text(`Generated: ${new Date().toLocaleDateString('en-AU')}`, 105, 28, { align: 'center' });
                                
                                let y = 40;
                                
                                // Meeting Minutes
                                doc.setFontSize(12);
                                doc.setFont(undefined, 'bold');
                                doc.text('Meeting Minutes', 14, y);
                                y += 8;
                                doc.setFontSize(9);
                                doc.setFont(undefined, 'normal');
                                if (meetingMinutes.length === 0) {
                                    doc.text('No meeting minutes recorded', 14, y);
                                    y += 5;
                                } else {
                                    meetingMinutes.forEach(m => {
                                        doc.text(`• ${m.date}: ${m.agenda}`, 14, y);
                                        y += 5;
                                    });
                                }
                                y += 5;
                                
                                // Resolutions
                                doc.setFontSize(12);
                                doc.setFont(undefined, 'bold');
                                doc.text('Trustee Resolutions', 14, y);
                                y += 8;
                                doc.setFontSize(9);
                                doc.setFont(undefined, 'normal');
                                if (resolutions.length === 0) {
                                    doc.text('No resolutions recorded', 14, y);
                                    y += 5;
                                } else {
                                    resolutions.forEach(r => {
                                        doc.text(`• ${r.title} (${r.date})`, 14, y);
                                        y += 5;
                                    });
                                }
                                y += 5;
                                
                                // Documents
                                doc.setFontSize(12);
                                doc.setFont(undefined, 'bold');
                                doc.text('Stored Documents', 14, y);
                                y += 8;
                                doc.setFontSize(9);
                                doc.setFont(undefined, 'normal');
                                doc.text(`Total documents: ${documents.length}`, 14, y);
                                y += 5;
                                
                                // Tasks
                                doc.setFontSize(12);
                                doc.setFont(undefined, 'bold');
                                doc.text('Outstanding Tasks', 14, y);
                                y += 8;
                                doc.setFontSize(9);
                                doc.setFont(undefined, 'normal');
                                if (tasks.length === 0) {
                                    doc.text('No tasks pending', 14, y);
                                } else {
                                    tasks.forEach(t => {
                                        doc.text(`• ${t.title} (Due: ${t.dueDate}, Priority: ${t.priority})`, 14, y);
                                        y += 5;
                                    });
                                }
                                
                                doc.save(`SMSF_Administration_Report_${new Date().toISOString().split('T')[0]}.pdf`);
                            }}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                        >
                            Export Administration Report
                        </button>
                        <button 
                            onClick={() => window.location.reload()}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Update Status
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SMSFAdministrationManager;

